import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import { useCompetenciesApi } from './config';

// Yetkinlik Kütüphanesi sekmesi — 360° form şablonlarındaki "İlgili Yetkinlik"
// seçicisinin BESLENDİĞİ kataloğun yönetim ekranı (gerekçe: config'teki
// useCompetenciesApi yorumu). Liste + drawer düzeni FeedbackFormsTab'ın liste
// yarısını aynalar; detay görünümü yok, düzenleme drawer'da biter.
//
// Seviye tanımları (LevelDescriptorsJson): 1-5 ölçeğindeki her puanın
// gözlemlenebilir davranış karşılığı. Drawer'da 5 opsiyonel alan olarak açılır,
// kayıtta yalnız dolu olanlar {"1":"...","5":"..."} biçiminde JSON'a yazılır —
// hepsi boşsa null gider (boş obje saklamak "tanım var" yanılgısı yaratırdı).

const Hint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 12px;
`;

const LEVELS = [1, 2, 3, 4, 5];

function levelsFromJson(json) {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return Object.fromEntries(LEVELS.map((l) => [`level${l}`, parsed[String(l)] || '']));
  } catch {
    return {};
  }
}

function levelsToJson(values) {
  const filled = {};
  LEVELS.forEach((l) => {
    const v = (values[`level${l}`] || '').trim();
    if (v) filled[String(l)] = v;
  });
  return Object.keys(filled).length > 0 ? JSON.stringify(filled) : null;
}

export default function CompetenciesTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('PM');

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [busy, setBusy] = useState(false);

  const {
    fetching, data, pagination, columns: baseColumns,
    createCompetency, updateCompetency, deleteCompetency,
  } = useCompetenciesApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const openNew = useCallback(() => setDrawer({ open: true, row: null }), []);
  const openEdit = useCallback((row) => setDrawer({ open: true, row }), []);

  const actionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      { type: 'edit', label: t('common.edit'), onClick: openEdit },
      { type: 'divider' },
      {
        type: 'delete',
        label: t('common.delete'),
        onClick: (row) => setDeleteConfirm({ isOpen: true, id: row.id, name: row.name }),
      },
    ];
  }, [canManage, t, openEdit]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const typeOptions = useMemo(() => [
    { id: 'behavioral', name: t('performance.competency.behavioral') },
    { id: 'technical', name: t('performance.competency.technical') },
  ], [t]);

  const schema = useMemo(() => [
    { name: 'name', label: t('performance.competency.name'), type: 'text', grid: 12, required: true },
    // P1 — opsiyonel grup başlığı (BE: trim + ≤100; 360 form soru seçicisinde
    // "Grup — Ad" etiketi bu alandan üretilir).
    { name: 'groupName', label: t('performance.competency.groupName'), type: 'text', grid: 12 },
    { name: 'competencyType', label: t('performance.competency.type'), type: 'select', grid: 12, required: true, options: typeOptions, defaultValue: 'behavioral' },
    { name: 'description', label: t('performance.common.description'), type: 'textarea', grid: 12, rows: 3 },
    ...LEVELS.map((l) => ({
      name: `level${l}`,
      label: t('performance.competency.levelLabel', { level: l }),
      type: 'textarea',
      grid: 12,
      rows: 2,
    })),
  ], [t, typeOptions]);

  const initialValues = useMemo(() => {
    if (!drawer.row) return null;
    return {
      name: drawer.row.name,
      groupName: drawer.row.groupName || '',
      competencyType: drawer.row.competencyType,
      description: drawer.row.description || '',
      ...levelsFromJson(drawer.row.levelDescriptorsJson),
    };
  }, [drawer.row]);

  const handleSave = useCallback(async (values) => {
    const payload = {
      name: values.name,
      groupName: values.groupName || null,
      competencyType: values.competencyType || 'behavioral',
      description: values.description || null,
      levelDescriptorsJson: levelsToJson(values),
    };
    return drawer.row
      ? await updateCompetency(drawer.row.id, payload)
      : await createCompetency(payload);
  }, [drawer.row, updateCompetency, createCompetency]);

  const confirmDelete = useCallback(async () => {
    setBusy(true);
    await deleteCompetency(deleteConfirm.id);
    setBusy(false);
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
  }, [deleteCompetency, deleteConfirm.id]);

  return (
    <div>
      <Hint>{t('performance.competency.hint')}</Hint>

      <GenericFormDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        title={drawer.row ? t('performance.competency.edit') : t('performance.competency.new')}
        schema={schema}
        initialValues={initialValues}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      <DataTable
        title={t('performance.competency.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canManage}
        onNewButtonClick={openNew}
        onRowClick={canManage ? openEdit : undefined}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />
    </div>
  );
}
